import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr, fmtRent, bedsLabel } from "../../lib/api";

const STAGES = [["new", "New"], ["contacted", "Contacted"], ["viewing_scheduled", "Viewing Scheduled"], ["viewed", "Viewed"], ["application", "Application"], ["leased", "Leased (Won)"], ["lost", "Lost"]];
const OUTREACH_BADGE = { draft: "bg-secondary", sent: "bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100", opened: "bg-teal-100 text-teal-900", replied: "bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100", referral_confirmed: "bg-green-600 text-white", declined: "bg-coral-100 text-coral-800" };

export default function LeadDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [note, setNote] = useState("");

  const load = () => api.get(`/admin/leads/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); api.get("/admin/users").then((r) => setAgents(r.data.filter((u) => ["agent", "admin"].includes(u.role)))).catch(() => {}); }, [id]); // eslint-disable-line

  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const { lead, viewings, outreach, listings } = data;
  const c = lead.criteria || {};

  const patch = async (body) => {
    try { await api.patch(`/admin/leads/${id}`, body); setNote(""); load(); toast.success("Saved"); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const rows = [
    ["Locations", (c.locations || []).join(", ")], ["Radius", c.radius_km ? `${c.radius_km} km` : null],
    ["Bedrooms", (c.beds || []).join(", ")], ["Bathrooms", (c.baths || []).join(", ")],
    ["Budget", c.budget_min ? `$${c.budget_min.toLocaleString()} – $${c.budget_max?.toLocaleString()}/mo` : null],
    ["Furnished", c.furnished], ["Parking", c.parking], ["Accessibility", (c.accessibility || []).join(", ")],
    ["Pets", (c.pets || []).join(", ")], ["Lease", c.lease_duration],
    ["Move-in", c.move_in_date], ["Move-out", c.move_out_date || "Open-ended"], ["Occupants", c.occupants],
    ["Note", c.note],
  ].filter(([, v]) => v);

  return (
    <div data-testid="lead-detail">
      <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary">← Back to pipeline</Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">{lead.first_name} {lead.last_name}</h1>
          <p className="text-sm text-muted-foreground">{lead.email} · {lead.phone}</p>
          <p className="text-xs text-muted-foreground mt-1">Consent given {lead.consent_at?.slice(0, 16).replace("T", " ")} · Source: {lead.source}</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="lead-detail-stage" value={lead.status} onChange={(e) => patch({ status: e.target.value })} aria-label="Lead stage"
            className="rounded-xl border border-input bg-background px-3 py-2 text-sm">
            {STAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="lead-detail-assign" value={lead.assigned_agent_id || ""} onChange={(e) => patch({ assigned_agent_id: e.target.value || null })} aria-label="Assign agent"
            className="rounded-xl border border-input bg-background px-3 py-2 text-sm">
            <option value="">Unassigned</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading font-semibold text-lg">Search criteria</h2>
          <dl className="mt-4 space-y-2">
            {rows.map(([k, v]) => (
              <div key={k} className="flex gap-3 text-sm"><dt className="w-28 shrink-0 text-muted-foreground">{k}</dt><dd className="font-medium">{String(v)}</dd></div>
            ))}
          </dl>
          {lead.note_signals?.summary && <p className="mt-4 rounded-xl bg-secondary px-4 py-3 text-xs"><span className="font-semibold">AI note parse:</span> {lead.note_signals.summary}</p>}
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading font-semibold text-lg">Chat transcript</h2>
          <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
            {(lead.chat_transcript || []).map((m, i) => (
              <p key={i} className={`text-xs rounded-xl px-3 py-2 ${m.role === "user" ? "bg-primary/10 ml-8" : "bg-secondary mr-8"}`}>
                <span className="font-semibold">{m.role === "user" ? "Renter" : "Tendly"}:</span> {m.text}
              </p>
            ))}
            {!lead.chat_transcript?.length && <p className="text-sm text-muted-foreground">No transcript captured.</p>}
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading font-semibold text-lg">Selected viewings</h2>
          {viewings.length ? (
            <ul className="mt-4 space-y-3">
              {viewings.map((v) => {
                const l = listings.find((x) => x.listing_id === v.listing_id);
                const o = outreach.find((x) => x.viewing_request_id === v.id);
                return (
                  <li key={v.id} className="rounded-xl border border-border p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold">{l ? `${l.address_street || l.neighborhood}${l.unit_number ? ` #${l.unit_number}` : ""}` : v.listing_id}</p>
                      <span className="text-xs text-muted-foreground">{l ? `${fmtRent(l.monthly_rent)}/mo · ${bedsLabel(l)}` : ""}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">Status: <span className="font-medium capitalize">{v.status}</span>{v.confirmed_time ? ` · Confirmed ${v.confirmed_time}` : ""}</p>
                    <p className="mt-1 text-xs text-muted-foreground">Availability: {(v.availability || []).map((s) => `${s.date} ${s.slot}`).join(" · ") || "flexible"}</p>
                    {o && <p className="mt-2 text-xs">Landlord outreach: <span className={`rounded-full px-2 py-0.5 font-semibold ${OUTREACH_BADGE[o.status] || "bg-secondary"}`}>{o.status}</span></p>}
                    {!o && <p className="mt-2 text-xs text-muted-foreground">No landlord email on file — contact by phone or portal.</p>}
                  </li>
                );
              })}
            </ul>
          ) : <p className="mt-4 text-sm text-muted-foreground">No viewing requests yet.</p>}
        </section>

        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-heading font-semibold text-lg">Notes & activity</h2>
          <div className="mt-4 space-y-3 max-h-56 overflow-y-auto">
            {(lead.notes || []).map((n) => (
              <div key={n.id} className="rounded-xl bg-secondary px-4 py-3 text-sm">
                <p>{n.text}</p><p className="mt-1 text-xs text-muted-foreground">{n.by} · {n.at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            ))}
            {!lead.notes?.length && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          </div>
          <div className="mt-4 flex gap-2">
            <input data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" aria-label="Add note"
              className="flex-1 rounded-xl border border-input bg-background px-4 py-2 text-sm" />
            <button data-testid="lead-note-add" onClick={() => note.trim() && patch({ note })} className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold">Add</button>
          </div>
        </section>
      </div>
    </div>
  );
}
